import type { EventKind, EventProgressDTO } from "../../domain/contracts";
import type { EventFeaturesDTO } from "../../domain/contracts/event-features";
import { eventHasFeature } from "../../domain/services/event-features";

/** Top-level tabs on the event detail page (lazy-loaded panels). */
export const EVENT_DETAIL_TAB_IDS = [
  "overview",
  "team",
  "staff",
  "pricing",
  "payout",
  "reports",
] as const;

export type EventDetailTabId = (typeof EVENT_DETAIL_TAB_IDS)[number];

/** Legacy section ids — used by deep links and progress card shortcuts. */
export const EVENT_DETAIL_SECTION_IDS = [
  "overview",
  "team",
  "pricing",
  "staff",
  "payout",
  "commission",
  "production_costs",
  "outsources",
] as const;

export type EventDetailSectionId = (typeof EVENT_DETAIL_SECTION_IDS)[number];

const SECTION_TO_TAB: Record<EventDetailSectionId, EventDetailTabId> = {
  overview: "overview",
  team: "team",
  staff: "staff",
  pricing: "pricing",
  payout: "payout",
  commission: "payout",
  production_costs: "payout",
  outsources: "payout",
};

export function resolveEventDetailTab(
  section: string | undefined,
): EventDetailTabId {
  if (!section) return "overview";
  if (EVENT_DETAIL_TAB_IDS.includes(section as EventDetailTabId)) {
    return section as EventDetailTabId;
  }
  if (EVENT_DETAIL_SECTION_IDS.includes(section as EventDetailSectionId)) {
    return SECTION_TO_TAB[section as EventDetailSectionId];
  }
  return "overview";
}

export function sectionToTab(section: EventDetailSectionId): EventDetailTabId {
  return SECTION_TO_TAB[section];
}

export function isEventDetailTabAvailable(
  tab: EventDetailTabId,
  features: EventFeaturesDTO,
): boolean {
  switch (tab) {
    case "overview":
      return true;
    case "team":
      return eventHasFeature(features, "performers");
    case "staff":
      return eventHasFeature(features, "staff");
    case "pricing":
      return eventHasFeature(features, "documents");
    case "payout":
      return (
        eventHasFeature(features, "performers") ||
        eventHasFeature(features, "staff") ||
        eventHasFeature(features, "commissions") ||
        eventHasFeature(features, "production_costs") ||
        eventHasFeature(features, "outsources")
      );
    case "reports":
      return (
        eventHasFeature(features, "performers") ||
        eventHasFeature(features, "rehearsals") ||
        eventHasFeature(features, "documents")
      );
    default:
      return false;
  }
}

export const EVENT_KIND_LABELS: Record<EventKind, string> = {
  stage_show: "ງານສະແດງ",
  mv_shoot: "ຖ່າຍ MV",
  film_ad: "ໂຄສະນາ / Film",
  content: "ຄອນເທັນ",
  kol: "KOL",
  mixed: "ປະສົມ",
};

export const BILLING_MODE_LABELS = {
  performance: "ງານສະແດງ",
  manual: "ງານ Event",
} as const;

export const BILLING_MODE_DESCRIPTIONS = {
  performance: "ຈັດທີມ → ເອກະສານລາຄາ → ເບີກຈ່າຍ",
  manual: "ເອກະສານມືລ້ວນ — ຈັດທີມ Staff ແລະ ເບີກຈ່າຍ",
} as const;

export function eventDisplayCode(id: string): string {
  return `EVT-${id.slice(0, 8).toUpperCase()}`;
}

/** Manual event — staff payout allowed after confirm or issued QT/INV. */
export function isStaffPayoutEventReady(event: {
  status: string;
  progress?: EventProgressDTO | null;
}): boolean {
  if (event.status === "confirmed" || event.status === "completed") {
    return true;
  }
  const milestones = event.progress?.milestones;
  if (!milestones) return false;
  if (milestones.pricing) return true;
  if ("invoiceIssued" in milestones && milestones.invoiceIssued) return true;
  return false;
}
